import React, { useState, useEffect } from 'react';
import { stockAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { DataTable } from '../components/DataTable';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Package, AlertTriangle, Pencil, Loader2, RefreshCw, IndianRupee, Store } from 'lucide-react';
import { toast } from 'sonner';
import { formatNumber, formatCurrency, formatDate } from '../lib/utils';

export const CurrentStockPage = () => {
  const [stock, setStock] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recalculating, setRecalculating] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedStock, setSelectedStock] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editFormData, setEditFormData] = useState({
    quantity: '',
    min_stock_level: '',
    unit_price: '',
    notes: ''
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const [stockRes, summaryRes] = await Promise.all([
        stockAPI.getAll(),
        stockAPI.getSummary()
      ]);
      setStock(stockRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      toast.error('Failed to fetch stock data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleRecalculateStock = async () => {
    try {
      setRecalculating(true);
      const response = await stockAPI.recalculate();
      toast.success(`Stock recalculated: ${response.data.updated} updated, ${response.data.created} created`);
      fetchData(); // Refresh the data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to recalculate stock');
    } finally {
      setRecalculating(false);
    }
  };

  const handleOpenEditDialog = (item) => {
    setSelectedStock(item);
    setEditFormData({
      quantity: item.quantity?.toString() || '0',
      min_stock_level: item.min_stock_level?.toString() || '0',
      unit_price: item.unit_price?.toString() || item.purchase_price?.toString() || '0',
      notes: ''
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async () => {
    if (!selectedStock) return;
    
    try {
      setSaving(true);
      const updateData = {
        quantity: editFormData.quantity ? parseFloat(editFormData.quantity) : null,
        min_stock_level: editFormData.min_stock_level ? parseFloat(editFormData.min_stock_level) : null,
        unit_price: editFormData.unit_price ? parseFloat(editFormData.unit_price) : null,
        notes: editFormData.notes || null
      };
      
      await stockAPI.update(selectedStock.id, updateData);
      toast.success('Stock updated successfully');
      setEditDialogOpen(false);
      setSelectedStock(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update stock');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Current Stock</h1>
          <p className="text-muted-foreground mt-1">View stock levels across all stores</p>
        </div>
        <Button 
          onClick={handleRecalculateStock} 
          variant="outline" 
          className="rounded-full" 
          disabled={recalculating}
          data-testid="recalculate-stock-btn"
        >
          {recalculating ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-2" />
          )}
          {recalculating ? 'Recalculating...' : 'Sync Stock'}
        </Button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card data-testid="summary-total-items">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-blue-100">
                  <Package className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Items</p>
                  <p className="text-2xl font-bold">{formatNumber(summary.total_items)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="summary-total-qty">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-green-100">
                  <Package className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Quantity</p>
                  <p className="text-2xl font-bold">{formatNumber(summary.total_quantity)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="summary-total-value">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-emerald-100">
                  <IndianRupee className="h-6 w-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Stock Value</p>
                  <p className="text-2xl font-bold">{formatCurrency(summary.total_stock_value || 0)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="summary-low-stock">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-amber-100">
                  <AlertTriangle className="h-6 w-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Low Stock Items</p>
                  <p className="text-2xl font-bold text-amber-600">{formatNumber(summary.low_stock_count)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="summary-stores">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-purple-100">
                  <Store className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Active Stores</p>
                  <p className="text-2xl font-bold">{summary.store_summary?.length || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Store-wise Stock Summary */}
      {summary && summary.store_summary && summary.store_summary.length > 0 && (
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Store className="h-5 w-5" />
              Store-wise Stock Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {summary.store_summary.map((store, index) => (
                <Card key={index} className="border shadow-sm">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-foreground">{store.store}</h4>
                      <Badge variant="secondary" className="text-xs">
                        {store.item_count} {store.item_count === 1 ? 'item' : 'items'}
                      </Badge>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Total Quantity:</span>
                        <span className="font-medium">{formatNumber(store.total_quantity)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Stock Value:</span>
                        <span className="font-bold text-emerald-600">{formatCurrency(store.total_value || 0)}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="pt-6">
          <DataTable
            module="stock"
            data={stock}
            loading={loading}
            title="Current Stock Report"
            columns={[
              { key: 'item_name', label: 'Item', render: (val) => <span className="font-medium">{val}</span> },
              { key: 'item_code', label: 'Code', render: (val) => val ? <span className="px-2 py-1 bg-muted rounded text-sm font-mono">{val}</span> : '-' },
              { key: 'category_name', label: 'Category' },
              { key: 'store_name', label: 'Store' },
              { key: 'property_name', label: 'Property' },
              { key: 'quantity', label: 'Quantity', render: (val, row) => {
                const isLow = val <= (row.min_stock_level || 0);
                return <span className={`font-medium ${isLow ? 'text-destructive' : ''}`}>{formatNumber(val)}</span>;
              }},
              { key: 'unit_name', label: 'Unit' },
              { key: 'unit_price', label: 'Unit Price', render: (val, row) => (
                <div className="flex items-center gap-1">
                  <span>{formatCurrency(val || 0)}</span>
                  {row.price_source === 'grn' && (
                    <Badge variant="outline" className="text-[10px] px-1 py-0 text-blue-600 border-blue-200">GRN</Badge>
                  )}
                  {row.price_source === 'stock_inward' && (
                    <Badge variant="outline" className="text-[10px] px-1 py-0 text-green-600 border-green-200">SI</Badge>
                  )}
                  {row.price_source === 'manual' && (
                    <Badge variant="outline" className="text-[10px] px-1 py-0 text-amber-600 border-amber-200">Manual</Badge>
                  )}
                </div>
              )},
              { key: 'stock_value', label: 'Stock Value', render: (val) => <span className="font-medium">{formatCurrency(val || 0)}</span> },
              { key: 'source', label: 'Source', render: (_, row) => {
                const sourceType = row.source;
                
                // GRN Source
                if (sourceType === 'grn' || (row.last_grn_number && row.last_po_number)) {
                  return (
                    <div className="text-xs space-y-0.5">
                      <Badge variant="outline" className="text-[10px] px-1 py-0 text-blue-600 border-blue-200 mb-1">GRN</Badge>
                      <div className="flex items-center gap-1">
                        <span className="text-muted-foreground">PO:</span>
                        <span className="font-mono font-medium text-blue-700">{row.last_po_number}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-muted-foreground">GRN:</span>
                        <span className="font-mono font-medium text-green-700">{row.last_grn_number}</span>
                      </div>
                    </div>
                  );
                }
                
                // Stock Inward Source
                if (sourceType === 'stock_inward' || row.last_si_number) {
                  return (
                    <div className="text-xs">
                      <Badge variant="outline" className="text-[10px] px-1 py-0 text-purple-600 border-purple-200 mb-1">Stock Inward</Badge>
                      <div>
                        <span className="text-muted-foreground">SI:</span>
                        <span className="font-mono font-medium text-purple-700 ml-1">{row.last_si_number}</span>
                      </div>
                    </div>
                  );
                }
                
                // Stock Transfer Source
                if (sourceType === 'stock_transfer' || row.last_transfer_number) {
                  return (
                    <div className="text-xs">
                      <Badge variant="outline" className="text-[10px] px-1 py-0 text-orange-600 border-orange-200 mb-1">Transfer</Badge>
                      {row.last_transfer_number && (
                        <div>
                          <span className="text-muted-foreground">SO:</span>
                          <span className="font-mono font-medium text-orange-700 ml-1">{row.last_transfer_number}</span>
                        </div>
                      )}
                    </div>
                  );
                }
                
                // Manual or Unknown
                if (sourceType === 'manual') {
                  return <Badge variant="outline" className="text-[10px] px-1 py-0 text-amber-600 border-amber-200">Manual Entry</Badge>;
                }
                
                return <span className="text-xs text-muted-foreground">Unknown</span>;
              }},
              { key: 'updated_at', label: 'Last Updated', render: (val) => val ? formatDate(val) : '-' },
              { key: 'needs_price_update', label: 'Flags', render: (val, row) => {
                if (val || row.flagged_for_finance) {
                  return (
                    <Badge variant="destructive" className="text-[10px]">
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      Needs Price
                    </Badge>
                  );
                }
                return null;
              }},
              { key: 'min_stock_level', label: 'Min Stock', render: (val) => formatNumber(val || 0) },
              { key: 'status', label: 'Status', render: (val, row) => {
                const isLow = row.quantity <= (row.min_stock_level || 0);
                return isLow ? (
                  <span className="flex items-center gap-1 text-amber-600 text-sm">
                    <AlertTriangle className="h-3 w-3" />Low
                  </span>
                ) : (
                  <Badge variant="outline" className="text-green-600 border-green-600">OK</Badge>
                );
              }},
            ]}
            renderActions={(item) => (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleOpenEditDialog(item)}
                title="Edit Stock"
              >
                <Pencil className="h-4 w-4" />
              </Button>
            )}
          />
        </CardContent>
      </Card>

      {/* Edit Stock Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="!w-[90vw] !max-w-[550px]">
          <DialogHeader>
            <DialogTitle>Edit Stock</DialogTitle>
            <DialogDescription>
              Adjust stock quantity and pricing for {selectedStock?.item_name} at {selectedStock?.store_name}
              {selectedStock?.price_source === 'grn' && (
                <span className="block mt-1 text-xs text-amber-600">
                  Price was set from GRN. You can override it manually.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Current Quantity</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={editFormData.quantity}
                onChange={(e) => setEditFormData({ ...editFormData, quantity: e.target.value })}
                placeholder="Enter quantity"
                data-testid="edit-stock-quantity"
              />
            </div>
            <div className="space-y-2">
              <Label>Unit Price (₹)</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={editFormData.unit_price}
                onChange={(e) => setEditFormData({ ...editFormData, unit_price: e.target.value })}
                placeholder="Enter unit price"
                data-testid="edit-stock-price"
              />
              <p className="text-xs text-muted-foreground">
                Stock Value = Quantity × Unit Price = {formatCurrency((parseFloat(editFormData.quantity) || 0) * (parseFloat(editFormData.unit_price) || 0))}
              </p>
            </div>
            <div className="space-y-2">
              <Label>Minimum Stock Level</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={editFormData.min_stock_level}
                onChange={(e) => setEditFormData({ ...editFormData, min_stock_level: e.target.value })}
                placeholder="Enter min stock level"
                data-testid="edit-stock-min-level"
              />
            </div>
            <div className="space-y-2">
              <Label>Adjustment Reason / Notes</Label>
              <Textarea
                value={editFormData.notes}
                onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                placeholder="e.g., Physical count correction, Price update, etc."
                rows={2}
                data-testid="edit-stock-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditSubmit} disabled={saving} data-testid="save-stock-edit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CurrentStockPage;
